import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useMe } from '../features/auth/useMe';
import { ErrorNotice } from './ErrorNotice';
import { LoadingScreen } from './LoadingScreen';

/**
 * Route guard for the platform-admin surface (ARCHITECTURE_PLAN §10: "/admin
 * requires platform admin"). Renders the guarded subtree ONLY when the
 * `GET /api/auth/me` bootstrap reports `user.isPlatformAdmin === true`.
 *
 * Behavior mirrors `RequireAuth` (which gates "session required"):
 *   - pending        → full-page loading state;
 *   - error          → full-page error with the safe message + requestId (no
 *                      blank failure state);
 *   - `user === null`→ redirect to /login with return state;
 *   - `user.isPlatformAdmin === false` → forbidden state naming the user so
 *                      a non-admin who hits /admin sees why they cannot proceed;
 *   - otherwise      → render the guarded subtree.
 *
 * An active impersonation session is also refused here: a platform admin who
 * is impersonating a tenant must NOT reach the admin console (they have
 * tenant-scoped capabilities MINUS billing, not admin capabilities, §6.4).
 * They are redirected to the impersonated tenant's workspace so the banner
 * can end the impersonation.
 */
export function RequirePlatformAdmin({ children }: { children: ReactNode }): React.JSX.Element {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) {
    return <LoadingScreen />;
  }

  if (me.isError) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md">
          <h1 className="text-lg font-semibold tracking-tight">Could not load your session</h1>
          <ErrorNotice error={me.error} className="mt-3" />
          <button
            type="button"
            onClick={() => {
              void me.refetch();
            }}
            className="mt-4 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (me.data.user === null) {
    // Admins sign in with a password (works even when OTP email is down).
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  if (me.data.session.isImpersonating) {
    // An impersonating admin has admin-MINUS-billing capabilities, not
    // platform-admin capabilities. Route them into the impersonated tenant.
    const slug = me.data.session.impersonatingTenantSlug;
    return <Navigate to={slug === null ? '/' : `/w/${slug}`} replace />;
  }

  if (!me.data.user.isPlatformAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Platform admin only</h1>
          <p className="mt-2 text-slate-600">
            You are signed in as <span className="font-medium">{me.data.user.email}</span>, but this
            account is not a platform admin.
          </p>
          <p className="mt-6 text-sm text-slate-600">
            <a href="/" className="font-medium text-slate-900 underline underline-offset-4">
              Go home
            </a>
          </p>
        </div>
      </main>
    );
  }

  return <>{children}</>;
}
